<template>
  <div 
    ref="wrapperElement"
    class="tooltip-wrapper"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @pointerdown="handleTriggerPointerDown"
  >
    <!-- Trigger Element (slot) -->
    <slot />
    
    <!-- Tooltip Content -->
    <Teleport to="body">
      <Transition
        enter-active-class="tooltip-enter-active"
        enter-from-class="tooltip-enter-from"
        enter-to-class="tooltip-enter-to"
        leave-active-class="tooltip-leave-active"
        leave-from-class="tooltip-leave-from"
        leave-to-class="tooltip-leave-to"
      >
        <div
          v-if="isVisible"
          ref="tooltipElement"
          class="tooltip"
          :class="tooltipClasses"
          :style="tooltipStyles"
          role="tooltip"
          :aria-label="content"
        >
          <!-- Arrow -->
          <div 
            v-if="showArrow"
            class="tooltip-arrow"
            :class="arrowClasses"
          />
          
          <!-- Content -->
          <div class="tooltip-content">
            <slot name="content">
              {{ content }}
            </slot>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'

interface Props {
  content?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  trigger?: 'hover' | 'click' | 'focus'
  /** When trigger is hover, also show/hide on keyboard focus (focusin/focusout). */
  showOnFocus?: boolean
  /**
   * When true, opening this tooltip dismisses any other singleton tooltip (e.g. side panel tab rail).
   * Other tooltips default to false and are unchanged.
   */
  singleton?: boolean
  delay?: number
  showArrow?: boolean
  maxWidth?: string
  disabled?: boolean
  /** Extra classes on the teleported tooltip root (e.g. sd-pages for theme tokens). */
  portalClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top',
  trigger: 'hover',
  showOnFocus: false,
  singleton: false,
  delay: 200,
  showArrow: true,
  maxWidth: '20rem',
  disabled: false
})

// State
const isVisible = ref(false)
const tooltipElement = ref<HTMLElement | null>(null)
const wrapperElement = ref<HTMLElement | null>(null)
let showTimer: ReturnType<typeof setTimeout> | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null

/** After pointer down on trigger, block focusin from reopening tooltip (hover + showOnFocus). */
let suppressFocusOpenUntil = 0
const FOCUS_SUPPRESS_AFTER_POINTER_MS = 350

/** Module-level: only one singleton tooltip visible at a time. */
let singletonOwner: { hide: () => void } | null = null

// Computed properties
const tooltipClasses = computed(() => [
  'tooltip',
  `tooltip--${props.position}`,
  props.portalClass,
  {
    'tooltip--disabled': props.disabled
  }
])

const arrowClasses = computed(() => [
  'tooltip-arrow',
  `tooltip-arrow--${props.position}`
])

const tooltipStyles = computed(() => ({
  maxWidth: props.maxWidth
}))

// Methods
const clearTimers = () => {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

const showTooltip = () => {
  if (props.disabled) return
  
  clearTimers()
  
  showTimer = setTimeout(() => {
    if (props.singleton) {
      if (singletonOwner && singletonOwner.hide !== hideTooltipImmediate) {
        singletonOwner.hide()
      }
      singletonOwner = { hide: hideTooltipImmediate }
    }
    isVisible.value = true
    nextTick(() => {
      positionTooltip()
    })
  }, props.delay)
}

const hideTooltip = () => {
  clearTimers()
  
  hideTimer = setTimeout(() => {
    isVisible.value = false
  }, 100)
}

/** Hide tooltip immediately (no delay). Used when trigger is activated (e.g. mousedown before menu opens). */
const hideTooltipImmediate = () => {
  clearTimers()
  isVisible.value = false
  if (props.singleton && singletonOwner?.hide === hideTooltipImmediate) {
    singletonOwner = null
  }
}

const positionTooltip = () => {
  if (!tooltipElement.value || !wrapperElement.value) return
  
  const tooltip = tooltipElement.value
  const trigger = wrapperElement.value.firstElementChild as HTMLElement
  if (!trigger) return
  
  const rect = trigger.getBoundingClientRect()
  const tooltipRect = tooltip.getBoundingClientRect()
  const scrollX = window.pageXOffset || document.documentElement.scrollLeft
  const scrollY = window.pageYOffset || document.documentElement.scrollTop
  
  let top = 0
  let left = 0
  
  switch (props.position) {
    case 'top':
      top = rect.top + scrollY - tooltipRect.height - 8
      left = rect.left + scrollX + (rect.width / 2) - (tooltipRect.width / 2)
      break
    case 'bottom':
      top = rect.bottom + scrollY + 8
      left = rect.left + scrollX + (rect.width / 2) - (tooltipRect.width / 2)
      break
    case 'left':
      top = rect.top + scrollY + (rect.height / 2) - (tooltipRect.height / 2)
      left = rect.left + scrollX - tooltipRect.width - 8
      break
    case 'right':
      top = rect.top + scrollY + (rect.height / 2) - (tooltipRect.height / 2)
      left = rect.right + scrollX + 8
      break
  }
  
  // Keep tooltip within viewport
  const padding = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  
  if (left < padding) {
    left = padding
  } else if (left + tooltipRect.width > viewportWidth - padding) {
    left = viewportWidth - tooltipRect.width - padding
  }
  
  if (top < padding) {
    top = padding
  } else if (top + tooltipRect.height > viewportHeight - padding) {
    top = viewportHeight - tooltipRect.height - padding
  }
  
  tooltip.style.top = `${top}px`
  tooltip.style.left = `${left}px`
}

const handleMouseEnter = () => {
  if (props.trigger === 'hover' && !props.disabled) {
    showTooltip()
  }
}

const handleMouseLeave = () => {
  if (props.trigger !== 'hover') return
  if (!props.showOnFocus) {
    hideTooltip()
    return
  }
  const trigger = wrapperElement.value?.firstElementChild as HTMLElement | undefined
  const active = typeof document !== 'undefined' ? document.activeElement : null
  if (!trigger || active !== trigger) {
    hideTooltip()
  }
}

const handleFocusIn = () => {
  if (props.disabled) return
  if (
    props.showOnFocus &&
    props.trigger === 'hover' &&
    typeof performance !== 'undefined' &&
    performance.now() < suppressFocusOpenUntil
  ) {
    return
  }
  if (props.trigger === 'focus' || (props.showOnFocus && props.trigger === 'hover')) {
    showTooltip()
  }
}

const handleFocusOut = () => {
  suppressFocusOpenUntil = 0
  if (props.trigger === 'focus' || (props.showOnFocus && props.trigger === 'hover')) {
    hideTooltip()
  }
}

/**
 * Pointer down: hide open tooltip; suppress focusin reopen (click after hover).
 * Uses performance.now() for suppress window so focusin in same frame can be blocked.
 */
const handleTriggerPointerDown = () => {
  if (props.showOnFocus && props.trigger === 'hover') {
    suppressFocusOpenUntil = performance.now() + FOCUS_SUPPRESS_AFTER_POINTER_MS
  }
  if ((props.trigger === 'hover' || props.trigger === 'focus') && isVisible.value) {
    hideTooltipImmediate()
  }
}

const handleClick = () => {
  if (props.trigger === 'click' && !props.disabled) {
    if (isVisible.value) {
      hideTooltip()
    } else {
      showTooltip()
    }
  }
}

// Watch for visibility changes to reposition and release singleton when hidden (incl. delayed hide)
watch(isVisible, (newValue) => {
  if (!newValue && props.singleton && singletonOwner?.hide === hideTooltipImmediate) {
    singletonOwner = null
  }
  if (newValue) {
    nextTick(() => {
      positionTooltip()
      window.addEventListener('scroll', positionTooltip, true)
      window.addEventListener('resize', positionTooltip)
    })
  } else {
    window.removeEventListener('scroll', positionTooltip, true)
    window.removeEventListener('resize', positionTooltip)
  }
})

// Lifecycle
onMounted(() => {
  // Add click handler if trigger is click
  if (props.trigger === 'click') {
    nextTick(() => {
      if (wrapperElement.value) {
        const trigger = wrapperElement.value.firstElementChild as HTMLElement
        if (trigger) {
          trigger.addEventListener('click', handleClick)
        }
      }
    })
  }
})

onUnmounted(() => {
  clearTimers()
  window.removeEventListener('scroll', positionTooltip, true)
  window.removeEventListener('resize', positionTooltip)
  if (props.singleton && singletonOwner?.hide === hideTooltipImmediate) {
    singletonOwner = null
  }

  if (wrapperElement.value && props.trigger === 'click') {
    const trigger = wrapperElement.value.firstElementChild as HTMLElement
    if (trigger) {
      trigger.removeEventListener('click', handleClick)
    }
  }
})
</script>

<style scoped>
.tooltip-wrapper {
  display: inline-block;
  position: relative;
}

.tooltip {
  position: fixed;
  z-index: 10000;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-text);
  background-color: var(--color-surface-muted);
  border: 1px solid var(--color-border);
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  word-wrap: break-word;
  white-space: normal;
}

.tooltip--disabled {
  display: none;
}

.tooltip-content {
  position: relative;
  z-index: 1;
}

/* Arrow Styles */
.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}

.tooltip-arrow--top {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0 6px;
  border-color: var(--color-surface-muted) transparent transparent transparent;
}

.tooltip-arrow--bottom {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 0 6px 6px 6px;
  border-color: transparent transparent var(--color-surface-muted) transparent;
}

.tooltip-arrow--left {
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 0 6px 6px;
  border-color: transparent transparent transparent var(--color-surface-muted);
}

.tooltip-arrow--right {
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 6px 6px 0;
  border-color: transparent var(--color-surface-muted) transparent transparent;
}

/* Transitions */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out;
}

.tooltip-enter-from {
  opacity: 0;
}

.tooltip-enter-to {
  opacity: 1;
}

.tooltip-leave-from {
  opacity: 1;
}

.tooltip-leave-to {
  opacity: 0;
}

/* Position-specific transitions */
.tooltip--top .tooltip-enter-from {
  transform: translateY(4px);
}

.tooltip--top .tooltip-enter-to {
  transform: translateY(0);
}

.tooltip--bottom .tooltip-enter-from {
  transform: translateY(-4px);
}

.tooltip--bottom .tooltip-enter-to {
  transform: translateY(0);
}

.tooltip--left .tooltip-enter-from {
  transform: translateX(4px);
}

.tooltip--left .tooltip-enter-to {
  transform: translateX(0);
}

.tooltip--right .tooltip-enter-from {
  transform: translateX(-4px);
}

.tooltip--right .tooltip-enter-to {
  transform: translateX(0);
}

</style>

